import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { episodes, recommendations } from '@/mocks/episodes';
import EpisodeCard from '@/components/feature/EpisodeCard';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';
import VideoPlayer from './components/VideoPlayer';

export default function Detail() {
  const { episodeId } = useParams();
  const episode = episodes.find((ep) => ep.id === episodeId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [episodeId]);

  usePageMeta({
    title: episode
      ? `${episode.title} - 无颜之月动漫第一季全集剧集详情`
      : '剧集不存在 - 无颜之月动漫第一季全集',
    description: episode
      ? `${episode.title}：${episode.summary} 无颜之月第一季第${episode.number}集的完整剧情介绍、角色说明与在线观看入口。`
      : '该剧集不存在或已移除，请返回剧集分类页查看无颜之月第一季全集目录。',
    keywords: episode
      ? `无颜之月第${episode.number}集,${episode.tags.join(',')},无颜之月动漫,动漫剧情`
      : '无颜之月动漫,剧集分类',
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2000);
  };

  if (!episode) {
    return (
      <div className="w-full px-4 md:px-8 pt-32 pb-20 flex flex-col items-center text-center">
        <span className="w-20 h-20 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-300 mb-5">
          <i className="ri-file-forbid-line text-4xl"></i>
        </span>
        <h1 className="font-heading font-bold text-2xl text-foreground-50">该剧集不存在或已移除</h1>
        <p className="mt-3 text-sm text-foreground-400 max-w-md">
          你访问的剧集可能已被移除或链接有误。可以返回剧集分类页，查看无颜之月第一季的全部目录。
        </p>
        <Link
          to="/category"
          className="mt-7 inline-flex items-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-950 hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          返回剧集分类
        </Link>
      </div>
    );
  }

  const sorted = [...episodes].sort((a, b) => a.number - b.number);
  const index = sorted.findIndex((ep) => ep.id === episode.id);
  const prev = index > 0 ? sorted[index - 1] : null;
  const next = index < sorted.length - 1 ? sorted[index + 1] : null;
  const related = sorted.filter((ep) => ep.id !== episode.id).slice(0, 3);
  const favorited = isFavorite(episode.id);

  const share = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast('链接已复制到剪贴板');
    } catch {
      showToast('复制失败，请手动复制地址栏链接');
    }
  };

  return (
    <div className="w-full px-4 md:px-8 pt-24 md:pt-28">
      <nav className="flex items-center flex-wrap gap-1 text-xs text-foreground-500 mb-6">
        <Link to="/" className="hover:text-accent-400 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/category" className="hover:text-accent-400 transition-colors">
          剧集分类
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-300">{episode.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 lg:gap-8 items-start">
        <div>
          <div className="mb-5">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="rounded-md bg-primary-500/20 border border-primary-500/40 px-3 py-1 text-xs font-semibold text-primary-200">
                EP {String(episode.number).padStart(2, '0')}
              </span>
              <span
                className={`rounded-md px-3 py-1 text-xs font-medium ${
                  episode.status === '已更新'
                    ? 'bg-secondary-500/85 text-background-50'
                    : 'bg-background-800 text-foreground-300 border border-foreground-700/50'
                }`}
              >
                {episode.status}
              </span>
            </div>
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 leading-tight">
              {episode.title}
            </h1>
            <p className="mt-2 text-sm text-foreground-500 tracking-wide">{episode.subtitle}</p>
          </div>

          <VideoPlayer cover={episode.cover} title={episode.title} />

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => {
                toggleFavorite(episode.id);
                showToast(favorited ? '已取消收藏' : '已加入收藏');
              }}
              className={`inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                favorited
                  ? 'bg-accent-500 text-background-950'
                  : 'bg-background-900 border border-primary-900/50 text-foreground-100 hover:bg-background-800'
              }`}
            >
              <i className={favorited ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
              {favorited ? '已收藏' : '收藏本集'}
            </button>
            <button
              type="button"
              onClick={share}
              className="inline-flex items-center gap-2 rounded-md bg-background-900 border border-primary-900/50 px-5 py-2.5 text-sm font-medium text-foreground-100 hover:bg-background-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-share-line"></i>
              分享
            </button>
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { icon: 'ri-hashtag', label: '集数', value: `第 ${episode.number} 集` },
              { icon: 'ri-calendar-line', label: '发布日期', value: episode.releaseDate },
              { icon: 'ri-time-line', label: '时长', value: episode.duration },
              { icon: 'ri-price-tag-3-line', label: '类型', value: episode.tags[0] },
            ].map((item) => (
              <div key={item.label} className="rounded-lg bg-background-900 border border-primary-900/40 px-4 py-3">
                <p className="flex items-center gap-1.5 text-xs text-foreground-500">
                  <i className={item.icon}></i>
                  {item.label}
                </p>
                <p className="mt-1 text-sm font-medium text-foreground-100">{item.value}</p>
              </div>
            ))}
          </div>

          <section className="mt-10">
            <h2 className="font-heading font-bold text-xl text-foreground-50 mb-4">剧情简介</h2>
            <p className="text-sm md:text-base text-foreground-300 leading-relaxed">{episode.summary}</p>
          </section>

          <section className="mt-8">
            <h2 className="font-heading font-bold text-xl text-foreground-50 mb-4">详细剧情介绍</h2>
            <div className="space-y-4">
              {episode.synopsis.map((paragraph, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-400 leading-loose">
                  {paragraph}
                </p>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <h2 className="font-heading font-bold text-xl text-foreground-50 mb-4">本集登场角色</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {episode.characters.map((character) => (
                <div
                  key={character.name}
                  className="flex items-start gap-4 rounded-lg bg-background-900 border border-primary-900/40 p-4"
                >
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 text-background-50 font-heading font-bold text-lg shrink-0">
                    {character.name.slice(-1)}
                  </span>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-semibold text-foreground-50">{character.name}</h3>
                      <span className="rounded-full bg-secondary-500/15 border border-secondary-500/30 px-2 py-0.5 text-[11px] text-secondary-200">
                        {character.role}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-foreground-400 leading-relaxed">{character.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="lg:sticky lg:top-24 space-y-6">
          <div className="rounded-lg bg-background-900 border border-primary-900/40 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-100 mb-4">剧集目录</h2>
            <div className="max-h-[420px] overflow-y-auto pr-1 space-y-1.5">
              {sorted.map((ep) => (
                <Link
                  key={ep.id}
                  to={`/detail/${ep.id}`}
                  className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors cursor-pointer ${
                    ep.id === episode.id
                      ? 'bg-primary-500/25 border border-primary-500/50 text-accent-300'
                      : 'text-foreground-300 hover:bg-background-800'
                  }`}
                >
                  <span className="font-heading text-xs text-foreground-500 w-8 shrink-0">
                    {String(ep.number).padStart(2, '0')}
                  </span>
                  <span className="line-clamp-1 flex-1">
                    {ep.title.replace(/^第\d+集 · /, '')}
                  </span>
                  {ep.status === '待更新' && (
                    <i className="ri-time-line text-foreground-500 text-xs shrink-0"></i>
                  )}
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-lg bg-background-900 border border-primary-900/40 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-100 mb-4">上下集导航</h2>
            <div className="flex flex-col gap-3">
              {prev ? (
                <Link
                  to={`/detail/${prev.id}`}
                  className="group flex items-center gap-3 rounded-md bg-background-800/70 border border-primary-900/40 px-4 py-3 hover:border-primary-500/60 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-circle-line text-xl text-primary-300"></i>
                  <span className="min-w-0">
                    <span className="block text-[11px] text-foreground-500">上一集</span>
                    <span className="block text-sm text-foreground-200 line-clamp-1 group-hover:text-accent-400 transition-colors">
                      {prev.title}
                    </span>
                  </span>
                </Link>
              ) : (
                <div className="flex items-center gap-3 rounded-md bg-background-800/40 border border-primary-900/30 px-4 py-3 opacity-60">
                  <i className="ri-arrow-left-circle-line text-xl text-foreground-600"></i>
                  <span className="text-sm text-foreground-500">已经是第一集</span>
                </div>
              )}
              {next ? (
                <Link
                  to={`/detail/${next.id}`}
                  className="group flex items-center gap-3 rounded-md bg-background-800/70 border border-primary-900/40 px-4 py-3 hover:border-primary-500/60 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-circle-line text-xl text-primary-300 order-2"></i>
                  <span className="min-w-0 flex-1 order-1 text-right">
                    <span className="block text-[11px] text-foreground-500">下一集</span>
                    <span className="block text-sm text-foreground-200 line-clamp-1 group-hover:text-accent-400 transition-colors">
                      {next.title}
                    </span>
                  </span>
                </Link>
              ) : (
                <div className="flex items-center gap-3 rounded-md bg-background-800/40 border border-primary-900/30 px-4 py-3 opacity-60">
                  <i className="ri-arrow-right-circle-line text-xl text-foreground-600"></i>
                  <span className="text-sm text-foreground-500">已经是最后一集</span>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg bg-gradient-to-br from-primary-900/50 to-secondary-900/40 border border-primary-800/40 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-100 mb-2">同类型推荐</h2>
            <div className="space-y-3">
              {recommendations.slice(0, 3).map((rec) => (
                <Link key={rec.id} to="/category" className="flex items-center gap-3 group cursor-pointer">
                  <span className="w-12 h-16 rounded-md overflow-hidden border border-primary-800/50 shrink-0">
                    <img src={rec.cover} alt={`${rec.title} 推荐`} className="w-full h-full object-top object-cover" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground-100 group-hover:text-accent-400 transition-colors line-clamp-1">
                      {rec.title}
                    </span>
                    <span className="block text-xs text-secondary-300">{rec.genre}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>

      <section className="mt-12 md:mt-16">
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 mb-6">相关推荐 · 同季剧集</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {related.map((ep) => (
            <EpisodeCard key={ep.id} episode={ep} />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[70] rounded-full bg-background-800 border border-primary-500/50 px-5 py-2.5 text-sm text-foreground-50 animate-fade-up">
          <i className="ri-checkbox-circle-line text-accent-400 mr-1.5"></i>
          {toast}
        </div>
      )}
    </div>
  );
}